import { useNavigate } from "react-router-dom";
import { SearchAutocomplete } from "@/components/SearchAutocomplete";
import { useLanguage } from "@/hooks/use-language";
import { BrandIcon } from "@/components/BrandLogo";
import heroBanner from "@/assets/hero-banner.jpg";

export function HeroSection() {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const handleSearch = (searchTerm: string, location: string) => {
    const params = new URLSearchParams();
    if (searchTerm) params.set("q", searchTerm);
    if (location) params.set("location", location);
    navigate(`/browse?${params.toString()}`);
  };

  return (
    <section className="relative overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src={heroBanner}
          alt="Skilled professionals across Africa"
          className="h-full w-full object-cover brightness-125 contrast-110 saturate-110"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
      </div>

      {/* Content */}
      <div className="container relative z-10 px-4 py-16 md:py-24 lg:py-32">
        <div className="max-w-3xl">
          {/* Badge */}
          <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 shadow-lg shadow-primary/30 border border-white/20">
            <BrandIcon size="sm" className="bg-transparent" iconClassName="text-white h-5 w-5" />
            <span className="text-sm font-bold text-white">
              {t.home.badgeText}
            </span>
          </div>

          {/* Headline */}
          <h1 className="mb-6 font-display text-4xl font-extrabold tracking-tight text-white drop-shadow-2xl md:text-5xl lg:text-6xl">
            {t.home.heroHeadline}
          </h1>

          {/* Subheadline */}
          <p className="mb-8 max-w-2xl text-lg font-semibold text-white/95 drop-shadow-xl md:text-xl leading-relaxed">
            {t.home.heroDescription}
          </p>

          {/* Search Box with Autocomplete */}
          <div className="drop-shadow-2xl">
            <SearchAutocomplete onSearch={handleSearch} />
          </div>

          {/* Trust Indicators */}
          <div className="mt-8 flex flex-wrap items-center gap-6 text-white">
            <div className="flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 backdrop-blur-sm border border-white/10">
              <span className="text-xl">✅</span>
              <span className="text-sm font-bold drop-shadow-md">15,000+ {t.home.verifiedPros}</span>
            </div>
            <div className="flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 backdrop-blur-sm border border-white/10">
              <span className="text-xl">⭐</span>
              <span className="text-sm font-bold drop-shadow-md">4.8 {t.home.averageRating}</span>
            </div>
            <div className="flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 backdrop-blur-sm border border-white/10">
              <span className="text-xl">🌍</span>
              <span className="text-sm font-bold drop-shadow-md">{t.home.allStates}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
